import client from "./client";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
  preferred_language?: string;
  preferred_theme?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  avatar_url: string | null;
  preferred_language: string;
  preferred_theme: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserProfile;
}

export interface MessageResponse {
  message: string;
}

// ─── Token helpers ───────────────────────────────────────────────────────────

const ACCESS_TOKEN_KEY = "katalog-access-token";
const REFRESH_TOKEN_KEY = "katalog-refresh-token";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// ─── API functions ───────────────────────────────────────────────────────────

/**
 * Register a new user account.
 * Stores tokens on success and returns the auth response.
 */
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const response = await client.post<AuthResponse>("/auth/register", data);
  const authData = response.data;
  setTokens(authData.access_token, authData.refresh_token);
  return authData;
}

/**
 * Log in with email and password.
 * Stores tokens on success and returns the auth response.
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await client.post<AuthResponse>("/auth/login", data);
  const authData = response.data;
  setTokens(authData.access_token, authData.refresh_token);
  return authData;
}

/**
 * Refresh the access token using a valid refresh token.
 * Updates stored tokens on success.
 */
export async function refresh(
  refreshToken?: string
): Promise<AuthResponse> {
  const token = refreshToken || getRefreshToken();
  if (!token) {
    throw new Error("No refresh token available");
  }

  const response = await client.post<AuthResponse>("/auth/refresh", {
    refresh_token: token,
  });

  const authData = response.data;
  setTokens(authData.access_token, authData.refresh_token);
  return authData;
}

/**
 * Log out the current user.
 * Clears stored tokens regardless of server response.
 */
export async function logout(): Promise<void> {
  try {
    await client.post<MessageResponse>("/auth/logout");
  } catch {
    // Logout should always succeed on the client side
  } finally {
    clearTokens();
  }
}

/**
 * Request a password reset email.
 * Always resolves successfully to prevent email enumeration.
 */
export async function forgotPassword(
  data: ForgotPasswordRequest
): Promise<MessageResponse> {
  const response = await client.post<MessageResponse>(
    "/auth/forgot-password",
    data
  );
  return response.data;
}

/**
 * Reset the password using a one-time token.
 */
export async function resetPassword(
  data: ResetPasswordRequest
): Promise<MessageResponse> {
  const response = await client.post<MessageResponse>(
    "/auth/reset-password",
    data
  );
  return response.data;
}

/**
 * Get the current authenticated user's profile.
 */
export async function getProfile(): Promise<UserProfile> {
  const response = await client.get<UserProfile>("/users/me");
  return response.data;
}

/**
 * Update the current authenticated user's profile.
 */
export async function updateProfile(
  data: UpdateProfileRequest
): Promise<UserProfile> {
  const response = await client.put<UserProfile>("/users/me", data);
  return response.data;
}

/**
 * Change the current user's password.
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<MessageResponse> {
  const response = await client.put<MessageResponse>(
    "/users/me/password",
    data
  );
  return response.data;
}
